import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { DocumentModel } from '@/api/models/document';
import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

// Enhanced document model with preview information
export interface EnhancedDocumentModel extends DocumentModel {
  preview_url?: string;
  download_url?: string;
  is_image?: boolean;
  is_pdf?: boolean;
  file_size_formatted?: string;
  file_extension?: string;
}

export type ApiRequest_GetDetails_Conversation = {
  conversation_id: string;
};

export type ApiResponse_GetDetails_Conversation = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  participant_count: number;
  participants: Array<{
    id: string;
    name: string;
  }>;
  messages: Array<{
    id: number;
    external_id: string;
    content: string;
    body: string;
    subject?: string;
    sender_name: string;
    sender: string;
    participant_sid: string | null;
    created_at: string;
    conversation_id: string;
    documents?: Array<EnhancedDocumentModel>;
  }>;
};

const METHOD = 'GET';
const ENDPOINT = 'conversation/:conversationSid';
export const QUERY_KEY = (params: ApiRequest_GetDetails_Conversation) =>
  getQueryKey(METHOD, ENDPOINT, params);

export const useGetConversationDetails = (
  params: ApiRequest_GetDetails_Conversation,
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!params.conversation_id,
    queryKey: QUERY_KEY(params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<
          ApiResponse_GetDetails_Conversation,
          ApiRequest_GetDetails_Conversation
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { conversationSid: params.conversation_id })
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch conversation details.', { variant: 'error' });
        throw err;
      }
    },
  });
};
